import { useState } from "react";
import { setAttendees, setSolo, type EventRow, type Person } from "../lib/eventsPage";
import { gameDate } from "../lib/format";
import { Avatar } from "./Avatar";
import { Sheet } from "./Sheet";

/**
 * Who went to one game: pick people, type a new name, or say you went alone.
 */
export function TagSheet({
  event,
  people,
  onClose,
  onSaved,
}: {
  event: EventRow;
  people: Person[];
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const [picked, setPicked] = useState<Set<number>>(new Set(event.attendee_ids));
  const [typed, setTyped] = useState("");
  const [newNames, setNewNames] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const q = typed.trim().toLowerCase();
  const shown = q ? people.filter((p) => p.name.toLowerCase().includes(q)) : people;
  const exact = people.some((p) => p.name.toLowerCase() === q) || newNames.some((n) => n.toLowerCase() === q);
  const count = picked.size + newNames.length;

  const toggle = (id: number) => {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setPicked(next);
  };

  async function run(action: () => Promise<void>, message: string) {
    setBusy(true);
    setError(null);
    try {
      await action();
      onSaved(message);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <Sheet title="Who went?" onClose={onClose}>
      <div className="grid gap-3">
        <p className="text-sm text-muted">
          {event.away_team} at {event.home_team} · {gameDate(event.event_date)}
        </p>
        <label className="grid gap-1 text-xs text-muted">
          Search, or add someone new
          <input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="Start typing a name…"
            className="rounded-lg border border-line bg-surface px-2.5 py-2 text-[15px] text-neutral-100"
          />
        </label>
        {q && !exact && (
          <button
            type="button"
            onClick={() => {
              setNewNames([...newNames, typed.trim()]);
              setTyped("");
            }}
            className="justify-self-start text-sm text-accent"
          >
            ＋ Add “{typed.trim()}”
          </button>
        )}
        {newNames.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {newNames.map((n) => (
              <span key={n} className="rounded-full bg-accent/15 px-2.5 py-0.5 text-[13px]">
                {n} (new)
              </span>
            ))}
          </div>
        )}
        <div className="grid max-h-72 gap-1.5 overflow-y-auto">
          {shown.map((p) => (
            <button
              key={p.id}
              type="button"
              aria-pressed={picked.has(p.id)}
              onClick={() => toggle(p.id)}
              className={`flex items-center gap-2.5 rounded-lg border px-3 py-2 text-left text-[15px] ${
                picked.has(p.id) ? "border-accent bg-accent/10" : "border-line"
              }`}
            >
              <Avatar name={p.name} url={p.avatar_url} />
              {p.name}
            </button>
          ))}
        </div>
        {error && <p className="text-sm text-bad">{error}</p>}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => run(() => setSolo(event.id, true), "Marked as went alone")}
            className="min-h-10 rounded-lg border border-line px-3.5 text-sm"
          >
            Went alone
          </button>
          <button
            type="button"
            disabled={busy || count === 0}
            onClick={() => run(() => setAttendees(event.id, [...picked], newNames), "Saved")}
            className="min-h-10 rounded-lg bg-neutral-100 px-3.5 text-sm font-semibold text-ground disabled:opacity-40"
          >
            Save{count ? ` ${count}` : ""}
          </button>
        </div>
      </div>
    </Sheet>
  );
}
